import { CheckCircle2, Circle, Edit3, Trash2 } from 'lucide-react';
import { PRIORITIES, TAGS } from '../constants';
import type { Todo } from '../types';
import { EmptyState } from './EmptyState';

interface Props {
  todos: Todo[];
  filter: string;
  search: string;
  onEdit: (todo: Todo) => void;
  onToggleDone: (todo: Todo) => void;
  onToggleEnabled: (todo: Todo) => void;
  onDelete: (todo: Todo) => void;
}

export function TodoView(props: Props) {
  const filtered = props.todos.filter((todo) => {
    const search = props.search.trim().toLocaleLowerCase();
    const matchesSearch = !search || `${todo.title} ${todo.note}`.toLocaleLowerCase().includes(search);
    const matchesFilter = props.filter === 'done' ? todo.isDone
      : props.filter === 'closed' ? !todo.isEnabled && !todo.isDone
        : todo.isEnabled && !todo.isDone;
    return matchesSearch && matchesFilter;
  });

  if (!filtered.length) {
    const title = props.search ? '找不到符合的待辦'
      : props.filter === 'done' ? '還沒有完成的待辦'
        : props.filter === 'closed' ? '還沒有關閉的待辦' : '還沒有待辦';
    return <EmptyState title={title} detail={!props.search && props.filter === 'open' ? '點右下角 + 記下一件事' : undefined} />;
  }

  return (
    <section className="item-list todo-list" aria-label="待辦清單">
      {filtered.map((todo) => {
        const tag = TAGS.find((item) => item.value === todo.tag) ?? TAGS[0];
        const priority = PRIORITIES.find((item) => item.value === todo.priority) ?? PRIORITIES[1];
        const dimmed = todo.isDone || !todo.isEnabled;
        return (
          <article key={todo.id} className={`item-card todo-card ${dimmed ? 'dimmed' : ''}`}>
            <span className="card-accent" style={{ backgroundColor: tag.color }} />
            <button
              type="button"
              className="todo-check"
              onClick={() => props.onToggleDone(todo)}
              aria-label={todo.isDone ? '標記未完成' : '標記完成'}
            >
              {todo.isDone ? <CheckCircle2 size={23} /> : <Circle size={23} />}
            </button>
            <button className="card-main todo-main" type="button" onClick={() => props.onEdit(todo)}>
              <span className="todo-title-line">
                {dimmed && <span className="status-badge">{todo.isDone ? '已完成' : '已關閉'}</span>}
                <strong className={todo.isDone ? 'struck' : ''}>{todo.title}</strong>
              </span>
              {todo.note && <span className="card-note">{todo.note}</span>}
              <span className="card-meta">
                <span style={{ color: tag.color }}>{tag.label}</span>
                <span style={{ color: priority.color }}>· 優先級{priority.label}</span>
              </span>
            </button>
            <div className="card-controls compact-controls">
              <label className="switch" aria-label={todo.isEnabled ? '關閉待辦' : '啟用待辦'}>
                <input type="checkbox" checked={todo.isEnabled} onChange={() => props.onToggleEnabled(todo)} />
                <span />
              </label>
              <div className="mini-actions">
                <button type="button" onClick={() => props.onEdit(todo)} aria-label="編輯"><Edit3 size={15} /></button>
                <button className="danger-icon" type="button" onClick={() => props.onDelete(todo)} aria-label="刪除"><Trash2 size={15} /></button>
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}
